<template>
  <!-- AI summary prototype (ENT-3361): works on mock data from TranscriptService.*Mock methods. -->
  <div class="h-[calc(100vh-106px)] bg-[#0c1116] flex" data-testid="ai-transcripts-view">
    <transcripts-sidebar
      v-if="!$isMobile() || !selectedId"
      class="w-[240px] shrink-0 max-[800px]:w-full"
      :transcripts="transcripts"
      :selected-id="selectedId"
      :loading="listLoading"
      @select="selectTranscript"
    />

    <main v-if="!$isMobile() || selectedId" class="flex-1 min-w-0 flex flex-col min-h-0 px-5 pt-5 pb-6 max-[800px]:px-4 max-[1000px]:overflow-y-auto">
      <button v-if="$isMobile()" type="button" class="self-start text-primary text-[13px] font-semibold mb-3 cursor-pointer" @click="backToList">
        {{ translate('common_back', 'Back') }}
      </button>

      <nav class="text-[11px] text-white/75" data-testid="ai-transcripts-breadcrumb">
        <router-link :to="{ name: 'ai-transcripts' }" class="hover:text-white">{{ translate('transcripts_all_events', 'All events') }}</router-link>
        <template v-if="selectedMeta"> / {{ selectedMeta.title }}</template>
      </nav>
      <h1 class="shrink-0 text-[22px] font-semibold text-white mt-1 truncate">
        {{ selectedMeta ? selectedMeta.title : $t('transcripts_title') }}
      </h1>
      <div class="h-px bg-[#1e2428] mt-4 mb-4"></div>

      <div class="flex-1 min-h-0 grid grid-cols-2 gap-3 max-[1000px]:grid-cols-1 max-[1000px]:flex-none">
        <summary-panel
          class="max-[1000px]:h-auto"
          :transcript="transcript"
          :state="transcript ? summaryState(transcript.conference_id) : null"
          @generate="generateSummary(transcript.conference_id)"
          @cancel="cancelSummary(transcript.conference_id)"
          @jump="jumpToTranscript"
          @feedback="sendFeedback"
        />
        <transcript-panel
          class="max-[1000px]:h-[70vh]"
          :transcript="transcript"
          :loading="transcriptLoading"
          :highlight="highlight"
        />
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import TranscriptService from '@/services/transcript.service';
import TranscriptsSidebar from '@/components/transcripts/TranscriptsSidebar.vue';
import SummaryPanel from '@/components/transcripts/SummaryPanel.vue';
import TranscriptPanel from '@/components/transcripts/TranscriptPanel.vue';
import { useTranscriptSummary } from '@/composables/useTranscriptSummary';
import { translate } from '@/composables/useCaptionSettings';
import { toast } from '@/plugins/toast';

const route = useRoute();
const router = useRouter();
const { summaryState, loadSummary, generateSummary, cancelSummary, setSummaryFeedback } = useTranscriptSummary();

const transcripts = ref([]);
const listLoading = ref(true);
const transcript = ref(null);
const transcriptLoading = ref(false);
const highlight = ref(null);

const selectedId = computed(() => route.params.id || null);
const selectedMeta = computed(() =>
  transcript.value || transcripts.value.find(item => item.conference_id === selectedId.value) || null
);

let transcriptRequest = 0;

async function loadTranscript(id) {
  const request = ++transcriptRequest;
  transcript.value = null;
  highlight.value = null;
  if (!id) return;

  transcriptLoading.value = true;
  try {
    const { data } = await TranscriptService.getTranscriptMock(id);
    if (request !== transcriptRequest) return;
    transcript.value = data.result;
    loadSummary(id);
  } catch (error) {
    if (request !== transcriptRequest) return;
    console.error(error.message);
    toast.error('error_unknown');
    router.replace({ name: 'ai-transcripts' });
  } finally {
    if (request === transcriptRequest) transcriptLoading.value = false;
  }
}

function selectTranscript(id) {
  if (id === selectedId.value) return;
  router.push({ name: 'ai-transcript', params: { id } });
}

function backToList() {
  router.push({ name: 'ai-transcripts' });
}

function jumpToTranscript({ start, end }) {
  highlight.value = { start, end, nonce: Date.now() };
}

async function sendFeedback(value) {
  try {
    await setSummaryFeedback(transcript.value.conference_id, value);
  } catch (error) {
    console.error(error.message);
    toast.error('error_unknown');
  }
}

watch(selectedId, loadTranscript);

onMounted(async () => {
  loadTranscript(selectedId.value);
  try {
    const { data } = await TranscriptService.getTranscriptListMock();
    transcripts.value = data.result.transcripts;
  } finally {
    listLoading.value = false;
  }
});
</script>
